import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ChevronDown, DoorOpen } from "lucide-react";
import Countdown from "@/components/Countdown";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const shapeY = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 16 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMouseMove} className="relative flex min-h-screen items-center overflow-hidden bg-[#FAFAF8]" data-testid="hero-section">
      <div className="bg-polygons absolute inset-0 opacity-70" aria-hidden="true" />
      <motion.div style={{ y: shapeY }} className="absolute -right-40 top-1/4 hidden h-[34rem] w-[34rem] rotate-12 rounded-[3rem] border-2 border-[#E30613]/15 lg:block" aria-hidden="true" />
      <div className="absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-[#E30613]/5 blur-3xl" aria-hidden="true" />

      <motion.div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-6 pt-32 pb-20 lg:grid-cols-[1.15fr_1fr]">
        <div>
          <MaskedLine delay={0.15}>
            <span className="inline-flex items-center gap-3 rounded-full border border-[#E30613]/30 bg-white px-5 py-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[#E30613] shadow-sm" data-testid="hero-date-badge">
              25 Ottobre 2026 · Open Day 10:00 — 17:00 · Cocquio-Trevisago (VA)
            </span>
          </MaskedLine>

          <MaskedLine delay={0.22} className="mt-6 max-w-xl">
            <div className="flex items-start gap-4 rounded-2xl border border-[#E30613]/25 bg-white/85 px-6 py-5 shadow-sm backdrop-blur-sm" data-testid="hero-openday-callout">
              <DoorOpen size={22} className="mt-0.5 shrink-0 text-[#E30613]" />
              <div className="space-y-1.5 text-base leading-relaxed text-[#3A3C3C]">
                <p><span className="font-bold text-[#E30613]">È un Open Day:</span> le nostre porte restano aperte dalle 10:00 alle 17:00.</p>
                <p className="font-light">Vieni nella fascia oraria che preferisci e resta tutto il tempo che vuoi.</p>
                <p className="font-light">Anche solo per bere qualcosa insieme!</p>
              </div>
            </div>
          </MaskedLine>

          <h1 className="sr-only">Tecnosystem 45 Anni — Open Day</h1>
          <MaskedLine delay={0.3} className="mt-8">
            <motion.img
              src="/logo-full.png"
              alt="Tecnosystem 45 anni di esperienza, il futuro è già qui"
              className="w-full max-w-md drop-shadow-[0_24px_40px_rgba(35,37,37,0.18)]"
              data-testid="hero-logo"
              initial={{ scale: 0.94 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            />
          </MaskedLine>

          <MaskedLine delay={0.55} className="mt-10 max-w-xl">
            <div className="space-y-4 text-lg font-bold leading-relaxed text-[#3A3C3C]" data-testid="hero-subtitle">
              <p>Un traguardo importante che racconta 45 anni di lavoro, crescita ed innovazione.</p>
              <p>Un percorso costruito giorno dopo giorno grazie alla fiducia dei nostri clienti, che ci hanno scelto e continuano a sceglierci, e grazie a tutte le persone che ogni giorno lavorano con impegno e passione in Tecnosystem.</p>
              <p>A chi ha fatto parte di questa storia e a chi continuerà a scriverla insieme a noi: <span className="text-[#E30613]">GRAZIE!</span></p>
            </div>
          </MaskedLine>

          <MaskedLine delay={0.7} className="mt-10">
            <div className="flex flex-wrap items-center gap-4">
              <a href="#rsvp" data-testid="hero-cta-button" className="rounded-full bg-[#E30613] px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-all duration-300 hover:bg-[#B00410] hover:shadow-[0_12px_32px_rgba(227,6,19,0.35)]">
                Conferma la tua presenza
              </a>
              <a href="#programma" data-testid="hero-secondary-cta" className="rounded-full border border-[#2B2D2D]/20 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#2B2D2D] transition-colors duration-300 hover:border-[#E30613] hover:text-[#E30613]">
                Scopri il programma
              </a>
            </div>
          </MaskedLine>

          <MaskedLine delay={0.85} className="mt-12">
            <Countdown />
          </MaskedLine>
        </div>

        <motion.div
          style={{ rotateX: rX, rotateY: rY, transformPerspective: 800 }}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="hidden justify-center lg:flex"
          data-testid="hero-rotating-badge"
        >
          <div className="relative flex h-80 w-80 items-center justify-center rounded-full bg-white shadow-[0_30px_60px_rgba(35,37,37,0.12)]">
            <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full animate-spin-slow">
              <defs>
                <path id="badgeCircle" d="M100,100 m-84,0 a84,84 0 1,1 168,0 a84,84 0 1,1 -168,0" />
              </defs>
              <text fill="#E30613" fontSize="11.5" letterSpacing="4.5" fontFamily="Bank Gothic, sans-serif" fontWeight="600">
                <textPath href="#badgeCircle">OPEN DAY · 25 OTTOBRE 2026 · TECNOSYSTEM · 45 ANNI ·</textPath>
              </text>
            </svg>
            <img src="/logo-mark.png" alt="Marchio 45TS Tecnosystem" className="h-20 w-auto object-contain" />
          </div>
        </motion.div>
      </motion.div>

      <motion.a
        href="#invito"
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-[#A9ABA9] transition-colors hover:text-[#E30613]"
        data-testid="hero-scroll-indicator"
        aria-label="Scorri all'invito"
      >
        <ChevronDown size={26} className="animate-bounce" />
      </motion.a>
    </section>
  );
};

export default Hero;
